/**
 * Apex Food Source Complete Dataset
 *
 * Comprehensive data for all Apex Food Source clients, production lines, and historical data.
 * This data structure serves:
 * 1. Dashboard UI with client details
 * 2. SLM training for AI-powered insights
 * 3. Historical trend analysis (1998-present, 25+ years experience)
 *
 * Founded: 1998 in Abbotsford, BC - Contract food manufacturer and co-packer
 */

export type TimePeriod = 'DoD' | 'WoW' | 'MoM' | 'QoQ' | 'YoY';

export interface TimeSeriesValue {
  current: number;
  DoD: number;
  WoW: number;
  MoM: number;
  QoQ: number;
  YoY: number;
}

export interface Client {
  id: string;
  name: string;
  segment: 'Food Service Distributors' | 'Retail Private Label' | 'QSR Chains' | 'Specialty Food Brands';
  address: string;
  city: string;
  stateProvince: string;
  clientSince: string;
  revenue: TimeSeriesValue;
  ordersYTD: number;
  volumeCases: TimeSeriesValue;
  productLines: string[];
  contractType: 'Exclusive' | 'Preferred' | 'Standard';
  isTopPerformer: boolean;
  isBottomPerformer: boolean;
}

export interface ProductionLine {
  name: string;
  capacity: number; // %
  oee: number; // %
  output: TimeSeriesValue; // cases
  revenue: TimeSeriesValue;
  products: string[];
}

export interface ClientSegment {
  segment: string;
  clientCount: number;
  revenue: TimeSeriesValue;
  ordersYTD: number;
  clients: Client[];
}

// Generate realistic client data
const generateClients = (segment: string, count: number, startIdx: number): Client[] => {
  const clients: Client[] = [];

  const clientNames: Record<string, string[]> = {
    'Food Service Distributors': [
      'National Foodservice Co.', 'Sysco Canada West', 'Gordon Food Service BC',
      'Pratts Food Service', 'Flanagan Foodservice', 'Columbia Foods',
      'Capital Foodservice', 'Northern Foods Distribution', 'Pacific Coast Foods',
      'Mountain Valley Distributors', 'Harbour Food Supply', 'Prairie Distributors'
    ],
    'Retail Private Label': [
      'Metro Brands Inc.', 'Loblaw Private Label', 'Sobeys Our Compliments',
      'Save-On-Foods Premium', 'Costco Kirkland', 'Western Grocery Corp.',
      'Nature\'s Path Foods', 'Choices Markets', 'Whole Foods 365',
      'IGA Select', 'Real Canadian Superstore', 'FreshCo Exclusive',
      'London Drugs Specialty', 'Thrifty Foods Select', 'Quality Foods Private',
      'Nesters Market Premium', 'Urban Fare Signature', 'Safeway Select'
    ],
    'QSR Chains': [
      'Pacific QSR Group', 'Tim Hortons Regional', 'A&W Western Canada',
      'White Spot Restaurants', 'Triple O\'s', 'Boston Pizza Supply',
      'Earls Kitchen Supply', 'Cactus Club Provisions', 'Joey Restaurant Group'
    ],
    'Specialty Food Brands': [
      'Artisan Foods Ltd.', 'Happy Planet Foods', 'Vega Nutritional',
      'Left Coast Naturals', 'Sunrise Soya Foods', 'Silver Hills Bakery Supply',
      'Rocky Mountain Specialty', 'Organic BC Brands', 'Farm to Table Foods',
      'Pacific Harvest', 'Coastal Artisan Co.'
    ],
  };

  const cities = ['Vancouver', 'Surrey', 'Burnaby', 'Richmond', 'Calgary', 'Edmonton', 'Toronto', 'Seattle', 'Portland'];
  const names = clientNames[segment] || clientNames['Specialty Food Brands'];

  for (let i = 0; i < count; i++) {
    const name = names[i % names.length];
    const city = cities[i % cities.length];
    const yearJoined = 1998 + Math.floor(Math.random() * 26);
    const baseRevenue = 1000000 + Math.random() * 8000000;
    const isTop = i < 2;
    const isBottom = i >= count - 2;

    clients.push({
      id: `APEX-${segment.substring(0, 3).toUpperCase()}-${String(startIdx + i + 1).padStart(3, '0')}`,
      name,
      segment: segment as Client['segment'],
      address: `${1000 + i * 100} Industrial Way`,
      city,
      stateProvince: city === 'Toronto' ? 'ON' : city === 'Calgary' || city === 'Edmonton' ? 'AB' : city === 'Seattle' || city === 'Portland' ? 'WA' : 'BC',
      clientSince: `${yearJoined}-${String(Math.floor(Math.random() * 12) + 1).padStart(2, '0')}-01`,
      revenue: {
        current: Math.round(baseRevenue),
        DoD: +(0.4 + Math.random() * 1.2).toFixed(1),
        WoW: +(0.9 + Math.random() * 2).toFixed(1),
        MoM: +(1.5 + Math.random() * 3).toFixed(1),
        QoQ: +(2.5 + Math.random() * 4).toFixed(1),
        YoY: +(4.0 + Math.random() * 6).toFixed(1),
      },
      ordersYTD: Math.round(50 + Math.random() * 300),
      volumeCases: {
        current: Math.round(50000 + Math.random() * 400000),
        DoD: +(0.5 + Math.random() * 1.5).toFixed(1),
        WoW: +(1.0 + Math.random() * 2.5).toFixed(1),
        MoM: +(1.8 + Math.random() * 3.5).toFixed(1),
        QoQ: +(3.0 + Math.random() * 5).toFixed(1),
        YoY: +(5.0 + Math.random() * 7).toFixed(1),
      },
      productLines: ['Sauces', 'Seasonings', 'Frozen Foods'].slice(0, 1 + Math.floor(Math.random() * 3)),
      contractType: i < 3 ? 'Exclusive' : i < 8 ? 'Preferred' : 'Standard',
      isTopPerformer: isTop,
      isBottomPerformer: isBottom,
    });
  }

  return clients;
};

export const APEX_SEGMENTS: ClientSegment[] = [
  {
    segment: 'Food Service Distributors',
    clientCount: 12,
    revenue: { current: 24500000, DoD: 0.8, WoW: 1.7, MoM: 2.7, QoQ: 4.3, YoY: 7.2 },
    ordersYTD: 1245,
    clients: generateClients('Food Service Distributors', 12, 0),
  },
  {
    segment: 'Retail Private Label',
    clientCount: 18,
    revenue: { current: 19800000, DoD: 0.9, WoW: 1.9, MoM: 2.9, QoQ: 4.8, YoY: 8.1 },
    ordersYTD: 892,
    clients: generateClients('Retail Private Label', 18, 12),
  },
  {
    segment: 'QSR Chains',
    clientCount: 8,
    revenue: { current: 12400000, DoD: 0.7, WoW: 1.4, MoM: 2.2, QoQ: 3.6, YoY: 5.9 },
    ordersYTD: 456,
    clients: generateClients('QSR Chains', 8, 30),
  },
  {
    segment: 'Specialty Food Brands',
    clientCount: 10,
    revenue: { current: 10300000, DoD: 1.2, WoW: 2.4, MoM: 3.8, QoQ: 6.1, YoY: 10.2 },
    ordersYTD: 678,
    clients: generateClients('Specialty Food Brands', 10, 38),
  },
];

export const APEX_PRODUCTION_LINES: ProductionLine[] = [
  {
    name: 'Sauces & Condiments',
    capacity: 92,
    oee: 86.5,
    output: { current: 680000, DoD: 0.9, WoW: 1.8, MoM: 2.9, QoQ: 4.6, YoY: 7.8 },
    revenue: { current: 18200000, DoD: 0.9, WoW: 1.8, MoM: 2.9, QoQ: 4.6, YoY: 7.8 },
    products: ['BBQ Sauce', 'Marinades', 'Salad Dressings', 'Hot Sauce', 'Pasta Sauce', 'Asian Sauces'],
  },
  {
    name: 'Dry Blends & Seasonings',
    capacity: 88,
    oee: 84.2,
    output: { current: 520000, DoD: 0.7, WoW: 1.5, MoM: 2.4, QoQ: 3.9, YoY: 6.5 },
    revenue: { current: 14800000, DoD: 0.7, WoW: 1.5, MoM: 2.4, QoQ: 3.9, YoY: 6.5 },
    products: ['Spice Blends', 'Rubs', 'Coating Mixes', 'Soup Bases', 'Gravy Mixes'],
  },
  {
    name: 'Frozen Prepared Foods',
    capacity: 78,
    oee: 82.1,
    output: { current: 890000, DoD: 1.1, WoW: 2.2, MoM: 3.4, QoQ: 5.5, YoY: 9.2 },
    revenue: { current: 19500000, DoD: 1.1, WoW: 2.2, MoM: 3.4, QoQ: 5.5, YoY: 9.2 },
    products: ['Frozen Entrees', 'Appetizers', 'Meal Kits', 'Pizza Toppings', 'Prepared Proteins'],
  },
  {
    name: 'Bakery & Snacks',
    capacity: 85,
    oee: 83.8,
    output: { current: 760000, DoD: 0.6, WoW: 1.3, MoM: 2.1, QoQ: 3.4, YoY: 5.6 },
    revenue: { current: 14500000, DoD: 0.6, WoW: 1.3, MoM: 2.1, QoQ: 3.4, YoY: 5.6 },
    products: ['Granola', 'Energy Bars', 'Cookie Dough', 'Bread Mixes', 'Muffin Batters'],
  },
];

// Aggregate metrics
export const APEX_METRICS = {
  totalClients: 48,
  totalRevenue: { current: 67000000, DoD: 0.8, WoW: 1.6, MoM: 2.5, QoQ: 4.1, YoY: 6.8 },
  ebitda: { current: 8710000, DoD: 0.6, WoW: 1.3, MoM: 2.1, QoQ: 3.5, YoY: 5.9 },
  unitsProduced: { current: 2850000, DoD: 0.9, WoW: 1.8, MoM: 2.8, QoQ: 4.5, YoY: 7.2 },
  oee: { current: 84.2, DoD: 0.3, WoW: 0.6, MoM: 1.1, QoQ: 1.8, YoY: 3.2 },
  batchYield: { current: 97.8, DoD: 0.1, WoW: 0.2, MoM: 0.3, QoQ: 0.5, YoY: 0.9 },
  fillRate: { current: 96.5, DoD: 0.2, WoW: 0.4, MoM: 0.6, QoQ: 1.0, YoY: 1.8 },
  firstPassRate: { current: 98.2, DoD: 0.1, WoW: 0.1, MoM: 0.2, QoQ: 0.4, YoY: 0.7 },
  costPerUnit: { current: 18.42, DoD: -0.1, WoW: -0.2, MoM: -0.4, QoQ: -0.7, YoY: -1.2 },
  leadTimeDays: { current: 12.5, DoD: -0.1, WoW: -0.2, MoM: -0.4, QoQ: -0.6, YoY: -1.1 },
};

// Quality metrics
export const APEX_QUALITY = [
  { metric: 'SQF Certification', score: 98.5, target: 95, status: 'Excellent' as const },
  { metric: 'Food Safety Incidents', score: 0, target: 0, status: 'Perfect' as const },
  { metric: 'Allergen Control', score: 99.8, target: 99, status: 'Excellent' as const },
  { metric: 'Customer Complaints (ppm)', score: 12, target: 25, status: 'Excellent' as const },
];

// Top performers
export const getTopClients = (count: number = 5): Client[] => {
  const allClients = APEX_SEGMENTS.flatMap(s => s.clients);
  return allClients
    .sort((a, b) => b.revenue.current - a.revenue.current)
    .slice(0, count);
};

// Bottom performers
export const getBottomClients = (count: number = 5): Client[] => {
  const allClients = APEX_SEGMENTS.flatMap(s => s.clients);
  return allClients
    .sort((a, b) => a.revenue.current - b.revenue.current)
    .slice(0, count);
};

// Company history
export const APEX_HISTORY = {
  founded: 1998,
  founder: 'Private ownership with Rooke family investment',
  headquarters: 'Abbotsford, BC',
  rookeConnection: 'Investment partner since 2008',
  certifications: ['SQF Level 3', 'HACCP', 'Organic', 'Non-GMO Project Verified', 'Kosher', 'Halal'],
  facilitySize: '150,000 sq ft',
  employees: 285,
  milestones: [
    { year: 1998, event: 'Founded in Abbotsford, BC - specialty sauce manufacturing' },
    { year: 2002, event: 'Expanded to dry blend production' },
    { year: 2005, event: 'Achieved SQF Level 2 certification' },
    { year: 2008, event: 'Rooke family became investment partner, facility expansion' },
    { year: 2010, event: 'Added frozen prepared foods line' },
    { year: 2012, event: 'Achieved SQF Level 3 certification' },
    { year: 2015, event: 'Opened bakery and snacks production facility' },
    { year: 2018, event: 'Reached 40 active clients, organic certification' },
    { year: 2020, event: 'Expanded capacity 30% to meet pandemic demand' },
    { year: 2023, event: '25th anniversary, 48 active clients, 285 employees' },
  ],
};
